import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight, Package, Store, Ticket } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';
import apiService from '../services/nextlevelgames-api';
import { visibleCategory } from '../utils/reportingCategory';
import { StockBadges } from '../components/ProductCard';

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, getTotalPrice, clearCart, fulfillmentPreference } = useCart();
  const { customer } = useUser();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [pendingOrderId, setPendingOrderId] = useState(
    () => sessionStorage.getItem('pendingOrderId') || null
  );
  const [showThankYou, setShowThankYou] = useState(false);
  const [completedOrderType, setCompletedOrderType] = useState(null);

  // Variation IDs the API has confirmed are event registrations. The cart is
  // persisted in localStorage, so an item can predate the is_event flag or
  // have been added by a surface that dropped it — in which case the cart
  // would offer Pickup/Shipping on an order checkout registers as DIGITAL.
  // Asking the API keeps this UI on the same source of truth as the checkout
  // guard. The local flag is still honoured so freshly added events render
  // correctly without waiting on the round trip.
  const [resolvedEventIds, setResolvedEventIds] = useState(() => new Set());
  // variation_id -> location_id for event lines, from the same lookup. Heals
  // cart lines saved without a location, which would otherwise fail checkout
  // with "No location ID available for order" until the cart was cleared.
  const [resolvedEventLocations, setResolvedEventLocations] = useState({});

  const isEventItem = (item) =>
    item.is_event === true || resolvedEventIds.has(item.variation_id);

  const hasEventItems = cartItems.some(isEventItem);
  const hasPhysicalItems = cartItems.some(item => !isEventItem(item));
  const allEventsCart = hasEventItems && !hasPhysicalItems;
  const mixedCart = hasEventItems && hasPhysicalItems;

  // Key on the variation IDs rather than cartItems so quantity changes don't
  // re-trigger the lookup.
  const cartVariationIds = cartItems.map(item => item.variation_id).filter(Boolean);
  const variationIdKey = cartVariationIds.join(',');

  useEffect(() => {
    const ids = variationIdKey ? variationIdKey.split(',') : [];
    if (ids.length === 0) {
      setResolvedEventIds(new Set());
      setResolvedEventLocations({});
      return;
    }
    let cancelled = false;
    apiService.resolveEventVariations(ids)
      .then(data => {
        if (cancelled) return;
        setResolvedEventIds(new Set(data.event_variation_ids || []));
        setResolvedEventLocations(data.event_locations || {});
      })
      // On failure keep whatever the local flags say. Checkout still forces
      // DIGITAL on an all-event order, so the worst case is a stale label,
      // never a shipping charge on a registration.
      .catch(() => {});
    return () => { cancelled = true; };
  }, [variationIdKey]);

  const [fulfillmentMethod, setFulfillmentMethod] = useState(allEventsCart ? 'digital' : (fulfillmentPreference?.method || 'shipping'));

  // Keep fulfillment in sync when cart contents change
  useEffect(() => {
    if (allEventsCart) setFulfillmentMethod('digital');
  }, [allEventsCart]);

  // When the tab regains focus, check whether the pending order was completed
  useEffect(() => {
    const onVisible = async () => {
      const orderId = sessionStorage.getItem('pendingOrderId');
      if (document.visibilityState !== 'visible' || !orderId) return;
      try {
        const response = await apiService.getOrder(orderId);
        const state = response?.order?.state;
        if (state && state !== 'DRAFT') {
          const orderType = sessionStorage.getItem('pendingOrderType');
          clearCart();
          sessionStorage.removeItem('pendingOrderId');
          sessionStorage.removeItem('pendingOrderType');
          setPendingOrderId(null);
          setCompletedOrderType(orderType);
          setShowThankYou(true);
        }
      } catch (err) {
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [clearCart]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locationError, setLocationError] = useState(null);


  const getAvailableLocationIds = () => {
    // Use tblinventory quantities as source of truth: a location is available if quantity > 0
    const locationSets = cartItems.map(item => {
      const qbl = item.quantities_by_location || {};
      const inStockIds = Object.entries(qbl)
        .filter(([, qty]) => qty > 0)
        .map(([locId]) => locId);
      // Fall back to location_id if inventory data is missing
      return inStockIds.length > 0 ? inStockIds : (item.location_id ? [item.location_id] : []);
    });

    if (locationSets.length === 0) return [];
    return locationSets.reduce((acc, curr) => acc.filter(id => curr.includes(id)));
  };

  // Items only conflict when no single store has all of them in stock. Each
  // item's location_id is just the store with the most stock when it was added,
  // so two items stocked at the same store can carry different ones.
  const hasMultipleLocations = () => {
    if (cartItems.length < 2) return false;
    return getAvailableLocationIds().length === 0;
  };

  // The store the order is placed at when not picking up: one that can fill the
  // whole cart, preferring the first item's own store.
  const getOrderLocationId = () => {
    const common = getAvailableLocationIds();
    const first = cartItems[0]?.location_id;
    if (first && common.includes(first)) return first;
    return common[0] || first || null;
  };

  useEffect(() => {
    const fetchLocations = async () => {
      if (fulfillmentMethod === 'pickup') {
        setLoadingLocations(true);
        setLocationError(null);
        try {
          const response = await apiService.getLocations();
          const allLocations = response.locations || response;
          const availableLocationIds = getAvailableLocationIds();
          const filteredLocations = allLocations.filter(loc => availableLocationIds.includes(loc.id));
          setLocations(filteredLocations);
          
          // Auto-select first location if only one exists
          if (filteredLocations.length === 1) {
            setSelectedLocation(filteredLocations[0]);
          } else if (filteredLocations.length === 0) {
            setLocationError('No locations have all items in your cart available for pickup');
          } else {
            // Start from the store chosen on the product page, if it can fill the whole cart
            const preferred = filteredLocations.find(loc => loc.id === fulfillmentPreference?.locationId);
            if (preferred) setSelectedLocation(prev => prev || preferred);
          }
        } catch (error) {
              setLocationError('Failed to load pickup locations');
        } finally {
          setLoadingLocations(false);
        }
      }
    };

    fetchLocations();
  }, [fulfillmentMethod, cartItems]);

  const validateCartItemsForLocation = (locationId) => {
    return cartItems.filter(item => {
      const qbl = item.quantities_by_location || {};
      const qty = qbl[locationId];
      // Invalid if we have inventory data and quantity at this location is 0 or missing
      if (Object.keys(qbl).length > 0) return !(qty > 0);
      // Fall back to location_id check if no inventory data
      return item.location_id && item.location_id !== locationId;
    });
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    const invalidItems = validateCartItemsForLocation(location.id);
    if (invalidItems.length > 0) {
      const itemNames = invalidItems.map(item => item.name).join(', ');
      setLocationError(`The following items are not available at this location: ${itemNames}`);
    } else {
      setLocationError(null);
    }
  };

  const hasInvalidItems = () => {
    if (fulfillmentMethod === 'pickup' && selectedLocation) {
      return validateCartItemsForLocation(selectedLocation.id).length > 0;
    }
    return false;
  };

  const isItemInvalidForLocation = (item) => {
    if (fulfillmentMethod === 'pickup' && selectedLocation) {
      const qbl = item.quantities_by_location || {};
      if (Object.keys(qbl).length > 0) return !(qbl[selectedLocation.id] > 0);
      return item.location_id && item.location_id !== selectedLocation.id;
    }
    return false;
  };

  // How many of an item are actually in stock at the location we'd fulfill from.
  // Returns null when we have no inventory data (don't restrict in that case).
  const getItemStockForFulfillment = (item) => {
    const qbl = item.quantities_by_location || {};
    if (Object.keys(qbl).length === 0) return null;
    if (fulfillmentMethod === 'pickup') {
      if (!selectedLocation) return null;
      return qbl[selectedLocation.id] ?? 0;
    }
    // shipping/digital: ceiling is total inventory across all locations
    return Object.values(qbl).reduce((sum, q) => sum + (q || 0), 0);
  };

  const itemExceedsStock = (item) => {
    const stock = getItemStockForFulfillment(item);
    return stock !== null && item.quantity > stock;
  };

  const hasQuantityIssues = () => cartItems.some(itemExceedsStock);

  // Max number of any single cart item available at a given location (the
  // limiting item). Used to show per-location availability in the selector.
  const getLocationAvailability = (locationId) => {
    const perItem = cartItems.map((item) => {
      const qbl = item.quantities_by_location || {};
      if (Object.keys(qbl).length === 0) return Infinity;
      return qbl[locationId] ?? 0;
    });
    return perItem.length ? Math.min(...perItem) : 0;
  };

  const handleCheckout = async () => {
    setIsCheckingOut(true);
    setCheckoutError(null);

    if (fulfillmentMethod === 'pickup') {
      if (!selectedLocation) {
        setCheckoutError('Please select a pickup location');
        setIsCheckingOut(false);
        return;
      }
    }

    try {
      const askForShipping = fulfillmentMethod === 'shipping';
      const customerId = customer?.id || null;
      const squareFulfillmentType = fulfillmentMethod === 'digital' ? 'DIGITAL'
        : fulfillmentMethod === 'shipping' ? 'SHIPMENT'
        : 'PICKUP';

      // Determine location ID
      let locationId = null;
      if (fulfillmentMethod === 'pickup' && selectedLocation) {
        locationId = selectedLocation.id;
      } else if (cartItems.length > 0) {
        locationId = getOrderLocationId()
          || resolvedEventLocations[cartItems[0].variation_id]
          || null;
      }

      const response = await apiService.createPaymentLink(cartItems, askForShipping, customerId, locationId, squareFulfillmentType);
      
      if (response.payment_link && response.payment_link.url) {
        const { url, order_id } = response.payment_link;
        sessionStorage.setItem('pendingOrderId', order_id);
        sessionStorage.setItem('pendingOrderType', allEventsCart ? 'event' : 'physical');
        setPendingOrderId(order_id);
        window.open(url, '_blank');
        setIsCheckingOut(false);
      } else {
        throw new Error('No payment link URL returned from API');
      }
    } catch (error) {
      setCheckoutError(error.message || 'Failed to create checkout. Please try again.');
      setIsCheckingOut(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center py-16">
            {showThankYou ? (
              <>
                <div className="text-6xl mb-6">🎉</div>
                <h1 className="text-3xl font-bold mb-4">Thank You for Choosing Next Level Games!</h1>
                <p className="text-gray-600 mb-3">
                  {completedOrderType === 'event'
                    ? "Your registration is confirmed. We can't wait to see you at the event!"
                    : 'Your order has been confirmed.'}
                </p>
                <p className="text-gray-500 text-sm mb-8">
                  A confirmation should be on its way to you shortly.
                </p>
              </>
            ) : (
              <>
                <ShoppingCart className="w-20 h-20 mx-auto text-gray-300 mb-6" />
                <h1 className="text-3xl font-bold mb-4">Your Cart is Empty</h1>
                <p className="text-gray-600 mb-8">
                  Looks like you haven't added anything to your cart yet.
                </p>
              </>
            )}
            <Link
              to={showThankYou && completedOrderType === 'event' ? '/events' : '/shop'}
              className="inline-flex items-center btn-primary"
            >
              {showThankYou
                ? completedOrderType === 'event' ? 'Browse More Events' : 'Continue Shopping'
                : 'Continue Shopping'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="container">
        <h1 className="text-3xl font-bold mb-8">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm">
              {cartItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-4 sm:p-6 border-b border-gray-200 last:border-0 ${
                    isItemInvalidForLocation(item) ? 'bg-red-50' : ''
                  }`}
                >
                  {isItemInvalidForLocation(item) && (
                    <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded-lg">
                      <p className="text-sm text-red-800 font-medium">
                        ⚠️ Not available at {selectedLocation.name}
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        This item is only available at a different location. Please remove it or change your pickup location.
                      </p>
                    </div>
                  )}
                  {!isItemInvalidForLocation(item) && itemExceedsStock(item) && (
                    <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded-lg">
                      <p className="text-sm text-red-800 font-medium">
                        ⚠️ Only {getItemStockForFulfillment(item)} in stock
                        {fulfillmentMethod === 'pickup' && selectedLocation ? ` at ${selectedLocation.name}` : ''}
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        You have {item.quantity} in your cart. Please reduce the quantity to continue.
                      </p>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                    <div className="flex gap-3 min-w-0 sm:contents">
                    {/* Product Image */}
                    <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg overflow-hidden">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-3xl">
                          🎴
                        </div>
                      )}
                    </div>

                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-base sm:text-lg mb-1 truncate">{item.name}</h3>
                      {item.variation_name && (
                        <p className="text-sm text-gray-600 font-medium mb-1 truncate">{item.variation_name}</p>
                      )}
                      {item.set && (
                        <p className="text-sm text-gray-500 mb-1 truncate">{item.set}</p>
                      )}
                      {visibleCategory(item.category) && (
                        <p className="text-sm text-gray-600">{item.category}</p>
                      )}
                      {!item.is_event && Object.keys(item.quantities_by_location || {}).length > 0 && (
                        <StockBadges quantities={item.quantities_by_location} />
                      )}
                    </div>

                    </div>

                    <div className="flex items-center justify-between gap-3 sm:contents">
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-1 rounded hover:bg-gray-100 transition-colors"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="px-3 py-1 bg-gray-100 rounded min-w-[40px] text-center font-medium">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        disabled={(() => {
                          const stock = getItemStockForFulfillment(item);
                          return stock !== null && item.quantity >= stock;
                        })()}
                        className="p-1 rounded hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Price */}
                    <div className="text-right flex-shrink-0">
                      <p className="font-semibold text-lg">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-500">
                        ${item.price.toFixed(2)} each
                      </p>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                      aria-label="Remove item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Clear Cart Button */}
              <div className="p-6 bg-gray-50">
                <button
                  onClick={clearCart}
                  className="text-red-600 hover:text-red-700 font-medium"
                >
                  Clear Cart
                </button>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm p-4 sm:p-6"
            >
              <h2 className="text-xl font-bold mb-4">Order Summary</h2>

              {/* Mixed Location Warning */}
              {hasMultipleLocations() && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <span className="text-yellow-600 text-lg">⚠️</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-yellow-800">No Store Has Everything</p>
                      <p className="text-xs text-yellow-700 mt-1">
                        These items aren't all in stock at the same store. Remove items until one store has them all to proceed.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* How to get it */}
              <div className="mb-6 pb-6 border-b">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-900">How to get it</h3>
                </div>

                {mixedCart && (
                  <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs text-yellow-800 font-medium">
                      Your cart contains event registrations and physical items. These cannot be checked out together. Please remove one type to continue.
                    </p>
                  </div>
                )}

                <div className={`grid gap-2 ${allEventsCart ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  {allEventsCart ? (
                    <div className="p-3 rounded-lg border-2 border-primary-600 bg-primary-50 text-primary-700 text-center">
                      <Ticket className="w-5 h-5 mx-auto mb-1" />
                      <div className="text-sm font-medium">Digital Registration</div>
                      <div className="text-xs text-primary-500 mt-0.5">No pickup or shipping needed</div>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => setFulfillmentMethod('pickup')}
                        disabled={hasEventItems}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          hasEventItems
                            ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                            : fulfillmentMethod === 'pickup'
                            ? 'border-primary-600 bg-primary-50 text-primary-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Store className="w-5 h-5 mx-auto mb-1" />
                        <div className="text-sm font-medium">Pickup</div>
                      </button>
                      <button
                        onClick={() => setFulfillmentMethod('shipping')}
                        disabled={hasEventItems}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          hasEventItems
                            ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                            : fulfillmentMethod === 'shipping'
                            ? 'border-primary-600 bg-primary-50 text-primary-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Package className="w-5 h-5 mx-auto mb-1" />
                        <div className="text-sm font-medium">Shipping</div>
                      </button>
                    </>
                  )}
                </div>
                {fulfillmentMethod === 'shipping' && (
                  <p className="text-xs text-gray-500 mt-2">Address will be collected at checkout</p>
                )}
                {fulfillmentMethod === 'pickup' && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Select pickup location</h4>
                    {loadingLocations ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                      </div>
                    ) : locations.length > 0 ? (
                      <div className="space-y-2">
                        {locations.map((location) => (
                          <button
                            key={location.id}
                            onClick={() => handleLocationSelect(location)}
                            className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                              selectedLocation?.id === location.id
                                ? 'border-primary-600 bg-primary-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="font-medium text-sm">{location.name}</div>
                            <div className="text-xs text-gray-600 mt-1">
                              {location.address.address_line_1}
                            </div>
                            <div className="text-xs text-gray-600">
                              {location.address.locality}, {location.address.administrative_district_level_1} {location.address.postal_code}
                            </div>
                            {location.phone_number && (
                              <div className="text-xs text-gray-500 mt-1">{location.phone_number}</div>
                            )}
                            {(() => {
                              const avail = getLocationAvailability(location.id);
                              if (!Number.isFinite(avail)) return null;
                              return (
                                <div className={`text-xs font-medium mt-1 ${avail > 0 ? 'text-gray-600' : 'text-red-600'}`}>
                                  {avail} available for pickup
                                </div>
                              );
                            })()}
                            {selectedLocation?.id === location.id && (
                              <div className="text-xs text-green-600 font-medium mt-1">✓ Selected</div>
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No pickup locations available</p>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>${getTotalPrice().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span>Calculated at checkout</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Tax</span>
                  <span>Calculated at checkout</span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span>${getTotalPrice().toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                disabled={
                  isCheckingOut ||
                  mixedCart ||
                  hasMultipleLocations() ||
                  hasQuantityIssues() ||
                  (fulfillmentMethod === 'pickup' && (!selectedLocation || hasInvalidItems()))
                }
                className="w-full btn-primary mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCheckingOut ? 'Processing...' : 'Proceed to Checkout'}
              </button>

              {hasMultipleLocations() && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">None of our stores has every item in your cart. Please remove items until at least one store has them all.</p>
                </div>
              )}

              {fulfillmentMethod === 'pickup' && !selectedLocation && (
                <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">Please select a pickup location to continue</p>
                </div>
              )}

              {fulfillmentMethod === 'pickup' && selectedLocation && hasInvalidItems() && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">Some items in your cart are not available at the selected location. Please remove them to continue.</p>
                </div>
              )}

              {!hasInvalidItems() && hasQuantityIssues() && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    {fulfillmentMethod === 'pickup'
                      ? 'One or more items exceed the quantity available at the selected location. Please reduce the quantity to continue.'
                      : 'One or more items exceed the quantity we have in stock. Please reduce the quantity to continue.'}
                  </p>
                </div>
              )}

              {checkoutError && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{checkoutError}</p>
                </div>
              )}

              <Link
                to="/shop"
                className="block text-center text-primary-600 hover:text-primary-700 font-medium"
              >
                Continue Shopping
              </Link>

            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
